/**
 * NotificationPanel Component
 * Matches the KCi App notification menu design system:
 * - Header: title + more (⋮) + close (×) on CB-10 background
 * - Search bar with All/Unread filter dropdown
 * - Activity feed with unread dot + avatar/status-icon + content + timestamp
 *
 * Consumes notifications from the global store via useNotifications hook,
 * which automatically fetches per-app data when the active app changes.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { formatNotificationTimestamp } from '../../utils';
import type { NotificationItem, NotificationKind } from '../../types';
import { kciEventBus } from '../../eventBus/KCiEventBus';
import { KCI_APP_IDS } from '../../eventBus/appIds';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type FilterKey = 'all' | 'unread';

type BrowserWindowWithSpeech = Window & {
  SpeechRecognition?: new () => {
    lang: string;
    interimResults: boolean;
    maxAlternatives: number;
    onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
    onerror: ((event: { error: string }) => void) | null;
    onend: (() => void) | null;
    start: () => void;
    stop: () => void;
  };
  webkitSpeechRecognition?: new () => {
    lang: string;
    interimResults: boolean;
    maxAlternatives: number;
    onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
    onerror: ((event: { error: string }) => void) | null;
    onend: (() => void) | null;
    start: () => void;
    stop: () => void;
  };
};

/** Status icon SVG paths mapped by notification kind */
const STATUS_ICON_MAP: Partial<Record<NotificationKind, { src: string; alt: string; bgClass: string }>> = {
  'action-required': {
    src: 'Status_Icons/Status Icon=Action Required, Type=Status Big.svg',
    alt: 'Action required',
    bgClass: 'notification-menu__activity-icon--action-required',
  },
  'step-complete': {
    src: 'Status_Icons/Status Icon=Step Complete, Type=Status Big.svg',
    alt: 'Completed',
    bgClass: 'notification-menu__activity-icon--step-complete',
  },
  'ready-review': {
    src: 'Status_Icons/Status Icon=Ready Review, Type=Status Big.svg',
    alt: 'Ready for review',
    bgClass: 'notification-menu__activity-icon--ready-review',
  },
  'profile': {
    src: 'Status_Icons/Status Icon=Information, Type=Status Big.svg',
    alt: 'Profile update',
    bgClass: 'notification-menu__activity-icon--profile',
  },
};

function renderStateIcon(notificationState?: string): React.ReactNode | null {
  const normalized = (notificationState ?? '').trim().toLowerCase();

  if (normalized === 'error') {
    return (
      <span className="notification-menu__activity-icon notification-menu__activity-icon--error-state" aria-label="Error">
        <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="20" cy="22" r="20" fill="#FFEAED" />
          <path d="M16.9993 26.1665L19.9993 23.1665L22.9993 26.1665L24.166 24.9998L21.166 21.9998L24.166 18.9998L22.9993 17.8332L19.9993 20.8332L16.9993 17.8332L15.8327 18.9998L18.8327 21.9998L15.8327 24.9998L16.9993 26.1665ZM19.9993 30.3332C18.8466 30.3332 17.7632 30.1144 16.7493 29.6769C15.7355 29.2394 14.8535 28.6457 14.1035 27.8957C13.3535 27.1457 12.7598 26.2637 12.3223 25.2498C11.8848 24.2359 11.666 23.1526 11.666 21.9998C11.666 20.8471 11.8848 19.7637 12.3223 18.7498C12.7598 17.7359 13.3535 16.854 14.1035 16.104C14.8535 15.354 15.7355 14.7603 16.7493 14.3228C17.7632 13.8853 18.8466 13.6665 19.9993 13.6665C21.1521 13.6665 22.2355 13.8853 23.2493 14.3228C24.2632 14.7603 25.1452 15.354 25.8952 16.104C26.6452 16.854 27.2389 17.7359 27.6764 18.7498C28.1139 19.7637 28.3327 20.8471 28.3327 21.9998C28.3327 23.1526 28.1139 24.2359 27.6764 25.2498C27.2389 26.2637 26.6452 27.1457 25.8952 27.8957C25.1452 28.6457 24.2632 29.2394 23.2493 29.6769C22.2355 30.1144 21.1521 30.3332 19.9993 30.3332ZM19.9993 28.6665C21.8605 28.6665 23.4368 28.0207 24.7285 26.729C26.0202 25.4373 26.666 23.8609 26.666 21.9998C26.666 20.1387 26.0202 18.5623 24.7285 17.2707C23.4368 15.979 21.8605 15.3332 19.9993 15.3332C18.1382 15.3332 16.5618 15.979 15.2702 17.2707C13.9785 18.5623 13.3327 20.1387 13.3327 21.9998C13.3327 23.8609 13.9785 25.4373 15.2702 26.729C16.5618 28.0207 18.1382 28.6665 19.9993 28.6665Z" fill="#ED2124" />
        </svg>
      </span>
    );
  }

  if (normalized === 'sharedinfo' || normalized === 'information' || normalized === 'info') {
    return (
      <span className="notification-menu__activity-avatar">
        <span className="notification-menu__activity-avatar-human" aria-hidden="true">
          <span className="material-symbols-outlined">person</span>
        </span>
      </span>
    );
  }

  if (normalized === 'success') {
    return (
      <span className="notification-menu__activity-icon notification-menu__activity-icon--success-state" aria-label="Success">
        <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="20" cy="22" r="20" fill="#E6F4E5" />
          <path d="M19.9993 30.3332C18.8466 30.3332 17.7632 30.1144 16.7493 29.6769C15.7355 29.2394 14.8535 28.6457 14.1035 27.8957C13.3535 27.1457 12.7598 26.2637 12.3223 25.2498C11.8848 24.2359 11.666 23.1526 11.666 21.9998C11.666 20.8471 11.8848 19.7637 12.3223 18.7498C12.7598 17.7359 13.3535 16.854 14.1035 16.104C14.8535 15.354 15.7355 14.7603 16.7493 14.3228C17.7632 13.8853 18.8466 13.6665 19.9993 13.6665C20.9021 13.6665 21.7563 13.7984 22.5618 14.0623C23.3674 14.3262 24.1105 14.6943 24.791 15.1665L23.5827 16.3957C23.0549 16.0623 22.4924 15.8019 21.8952 15.6144C21.298 15.4269 20.666 15.3332 19.9993 15.3332C18.1521 15.3332 16.5792 15.9825 15.2806 17.2811C13.982 18.5797 13.3327 20.1526 13.3327 21.9998C13.3327 23.8471 13.982 25.42 15.2806 26.7186C16.5792 28.0172 18.1521 28.6665 19.9993 28.6665C21.8466 28.6665 23.4195 28.0172 24.7181 26.7186C26.0167 25.42 26.666 23.8471 26.666 21.9998C26.666 21.7498 26.6521 21.4998 26.6244 21.2498C26.5966 20.9998 26.5549 20.7568 26.4994 20.5207L27.8535 19.1665C28.0063 19.6109 28.1243 20.0693 28.2077 20.5415C28.291 21.0137 28.3327 21.4998 28.3327 21.9998C28.3327 23.1526 28.1139 24.2359 27.6764 25.2498C27.2389 26.2637 26.6452 27.1457 25.8952 27.8957C25.1452 28.6457 24.2632 29.2394 23.2493 29.6769C22.2355 30.1144 21.1521 30.3332 19.9993 30.3332ZM18.8327 25.8332L15.291 22.2915L16.4577 21.1248L18.8327 23.4998L27.166 15.1457L28.3327 16.3123L18.8327 25.8332Z" fill="#269924" />
        </svg>
      </span>
    );
  }

  return null;
}

function isSharedStyleNotification(notif: NotificationItem): boolean {
  if (notif.kind === 'shared') {
    return true;
  }

  // step-complete and action-required notifications (e.g. accepted/declined results)
  // must never render as shared style — they have a description that must be shown.
  if (notif.kind === 'step-complete' || notif.kind === 'action-required') {
    return false;
  }

  const hasSharedMeta = Boolean(
    typeof notif.sender === 'string' && notif.sender.trim() &&
    typeof notif.verb === 'string' && notif.verb.trim() &&
    typeof notif.linkText === 'string' && notif.linkText.trim()
  );

  return hasSharedMeta;
}

type SharedNotificationRoute = 'shared-agent' | 'shared-prompt';

type NotificationRouteConfig = {
  targetToolTab: '1' | '2';
  targetPromptTab: '0' | '1';
  route: SharedNotificationRoute;
  resourceType: 'agent' | 'prompt';
};

const SHARED_NOTIFICATION_ROUTES: Record<'agent' | 'prompt', NotificationRouteConfig> = {
  agent: {
    targetToolTab: '1',
    targetPromptTab: '0',
    route: 'shared-agent',
    resourceType: 'agent',
  },
  prompt: {
    targetToolTab: '2',
    targetPromptTab: '1',
    route: 'shared-prompt',
    resourceType: 'prompt',
  },
};

function getSharedNotificationRoute(notif: NotificationItem): NotificationRouteConfig | null {
  const resourceType = String(notif.resourceType ?? '').toLowerCase();
  if (resourceType !== 'agent' && resourceType !== 'prompt') {
    const textBlob = [notif.verb, notif.description, notif.title, notif.linkText]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/shared\s+agent\b/.test(textBlob)) {
      return SHARED_NOTIFICATION_ROUTES.agent;
    }

    if (/shared\s+prompt\b/.test(textBlob)) {
      return SHARED_NOTIFICATION_ROUTES.prompt;
    }

    return null;
  }

  return SHARED_NOTIFICATION_ROUTES[resourceType];
}

interface NotificationDescriptionTooltipProps {
  text: string;
  children: React.ReactNode;
}

function NotificationDescriptionTooltip({ text, children }: NotificationDescriptionTooltipProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const tooltipWidth = Math.min(280, Math.max(180, window.innerWidth - 32));
    setPosition({
      top: Math.max(12, rect.top + rect.height / 2),
      left: Math.max(16, rect.left - tooltipWidth - 12),
    });
  }, []);

  useEffect(() => {
    // Position is set by the onMouseEnter/onFocus handlers before `open` flips true.
    if (!open) return;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return (
    <>
      <div
        ref={triggerRef}
        className="notification-menu__activity-tooltip-trigger"        
        tabIndex={0}
        onMouseEnter={() => { updatePosition(); setOpen(true); }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => { updatePosition(); setOpen(true); }}
        onBlur={() => setOpen(false)}
      >
        {children}
      </div>
      {open && createPortal(
        <div
          className="notification-menu__description-tooltip"
          role="tooltip"
          style={{ top: position.top, left: position.left }}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
}

interface CardActionsProps {
  notif: NotificationItem;
  onMarkReadOrUnread: (id: string, read: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const CardActions = React.memo(function CardActions({
  notif,
  onMarkReadOrUnread,
  onDelete,
}: CardActionsProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="notification-menu__card-actions" ref={menuRef}>
      <button
        className="notification-menu__card-action-btn"
        type="button"
        aria-label="Notification actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={e => {
          e.stopPropagation();
          setOpen(v => !v);
        }}
      >
        <span className="material-symbols-outlined">more_vert</span>
      </button>

      {open && (
        <div className="notification-menu__card-action-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="notification-menu__card-action-item notification-menu__card-action-item--mark"
            onClick={async e => {
              e.stopPropagation();
              setOpen(false);
              if (notif.read) {

                await onMarkReadOrUnread(notif.id, false);

              } else {

                await onMarkReadOrUnread(notif.id, true);

              }

            }}

          >

            <svg
              className="pulse-unread-ico"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.85 15C7.36667 15 7.82917 14.85 8.2375 14.55C8.64583 14.25 8.93333 13.8583 9.1 13.375L9.475 12.225C9.74167 11.425 9.675 10.6875 9.275 10.0125C8.875 9.3375 8.3 9 7.55 9H4.025L4.5 12.925C4.58333 13.5083 4.84583 14 5.2875 14.4C5.72917 14.8 6.25 15 6.85 15ZM17.15 15C17.75 15 18.2708 14.8 18.7125 14.4C19.1542 14 19.4167 13.5083 19.5 12.925L19.975 9H16.475C15.725 9 15.15 9.34167 14.75 10.025C14.35 10.7083 14.2833 11.45 14.55 12.25L14.9 13.375C15.0667 13.8583 15.3542 14.25 15.7625 14.55C16.1708 14.85 16.6333 15 17.15 15ZM6.85 17C5.75 17 4.7875 16.6375 3.9625 15.9125C3.1375 15.1875 2.65833 14.275 2.525 13.175L2 9H1V7H7.55C8.28333 7 8.95417 7.17917 9.5625 7.5375C10.1708 7.89583 10.65 8.38333 11 9H13.025C13.375 8.38333 13.8542 7.89583 14.4625 7.5375C15.0708 7.17917 15.7417 7 16.475 7H23V9H22L21.475 13.175C21.3417 14.275 20.8625 15.1875 20.0375 15.9125C19.2125 16.6375 18.25 17 17.15 17C16.2 17 15.3458 16.7292 14.5875 16.1875C13.8292 15.6458 13.3 14.925 13 14.025L12.625 12.9C12.5917 12.7833 12.5583 12.6625 12.525 12.5375C12.4917 12.4125 12.4583 12.2333 12.425 12H11.575C11.5417 12.2 11.5083 12.3625 11.475 12.4875C11.4417 12.6125 11.4083 12.7333 11.375 12.85L11 14C10.7 14.9 10.1708 15.625 9.4125 16.175C8.65417 16.725 7.8 17 6.85 17Z"
                fill="currentColor"
              />
            </svg>
            <span>{notif.read ? 'Mark as unread' : 'Mark as read'}</span>
          </button>

          <div className="notification-menu__card-action-divider" />

          <button
            type="button"
            role="menuitem"
            className="notification-menu__card-action-item notification-menu__card-action-item--delete"
            onClick={async e => {
              e.stopPropagation();
              setOpen(false);
              await onDelete(notif.id);
            }}
          >
            <span className="material-symbols-outlined">delete</span>
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
});

interface NotificationCardProps {
  notif: NotificationItem;
  onMarkReadOrUnread: (id: string, read: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

/** Renders a single notification activity card */
const NotificationCard = React.memo(function NotificationCard({
  notif,
  onMarkReadOrUnread,
  onDelete,
}: NotificationCardProps) {
  const statusIcon = STATUS_ICON_MAP[notif.kind];
  const stateIcon = renderStateIcon(notif.notificationState);
  const isShared = isSharedStyleNotification(notif);
  const sharedLinkText = typeof notif.linkText === 'string' ? notif.linkText.trim() : '';
  const sharedSummaryText = [notif.sender, notif.verb, sharedLinkText ? `'${sharedLinkText}'` : '']
    .filter(Boolean)
    .join(' ')
    .trim();
  const hasSharedSentence = Boolean(
    typeof notif.sender === 'string' && notif.sender.trim() &&
    typeof notif.verb === 'string' && notif.verb.trim() &&
    sharedLinkText
  );
  const fallbackTitle = (typeof notif.title === 'string' ? notif.title.trim() : '') || (!hasSharedSentence ? sharedLinkText : '');
  const fallbackDescription = (typeof notif.description === 'string' ? notif.description.trim() : '') || (!hasSharedSentence ? sharedSummaryText : '');
  const formattedTime = formatNotificationTimestamp(notif.createdAt > 0 ? notif.createdAt : 0);

  const handleActivate = useCallback(() => {
    if (!notif.read) {
      void onMarkReadOrUnread(notif.id, true);
    }

    const routeConfig = getSharedNotificationRoute(notif);
    if (routeConfig) {
      const eventPayload = {
        source: KCI_APP_IDS.SHELL,
        tabKey: 'context',
        selectedToolTab: routeConfig.targetToolTab,
        selectedPromptTab: routeConfig.targetPromptTab,
        activeUtilityTab: 'context',
        trigger: 'notification',
        resourceType: routeConfig.resourceType,
        notificationRoute: routeConfig.route,
        clickedAt: Date.now(),
      };

      kciEventBus.publish('ContextButtonClicked', eventPayload, {
        source: KCI_APP_IDS.SHELL,
        target: [KCI_APP_IDS.SHELL, KCI_APP_IDS.CLARA_REMOTE],
      });

      window.dispatchEvent(
        new CustomEvent('kci:openPromptFromNotification', {
          detail: {
            id: notif.id,
            resourceId: notif.resourceId,
            resourceType: routeConfig.resourceType,
            Properties: routeConfig.resourceType === 'prompt'
              ? { promptId: notif.resourceId }
              : { agentId: notif.resourceId },
            notificationRoute: routeConfig.route,
            linkText: notif.linkText,
            sender: notif.sender,
            description: notif.description,
          },
        })
      );
    }
  }, [notif, onMarkReadOrUnread]);

  return (
    <div
      className={`notification-menu__activity-item${!notif.read ? ' notification-menu__activity-item--unread' : ''}${isShared && !notif.read ? ' notification-menu__activity-item--right-unread-dot' : ''}`}
      data-notif-kind={notif.kind}
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      <span className="notification-menu__activity-dot" />

      {stateIcon ? stateIcon : isShared ? (
        <span className="notification-menu__activity-avatar">
          {notif.avatarSrc ? (
            <img src={notif.avatarSrc} alt={notif.sender ?? ''} />
          ) : (
            <span className="notification-menu__activity-avatar-human" aria-hidden="true">
              <span className="material-symbols-outlined">person</span>
            </span>
          )}
        </span>
      ) : statusIcon ? (
        <span className={`notification-menu__activity-icon ${statusIcon.bgClass}`}>
          <img src={statusIcon.src} alt={statusIcon.alt} width="24" height="24" />
        </span>
      ) : (
        <span className="notification-menu__activity-icon">
          <span className="material-symbols-outlined">notifications</span>
        </span>
      )}

      <div className="notification-menu__activity-content">
        {notif.agentName && (
          <div className="notification-menu__agent-label">{notif.agentName}</div>
        )}

        {isShared && hasSharedSentence ? (
          <>
            <NotificationDescriptionTooltip
              text={[notif.sender, notif.verb, sharedLinkText ? `'${sharedLinkText}'` : ''].filter(Boolean).join(' ')}
            >
              <div className="notification-menu__activity-text">
                <strong>{notif.sender}</strong>{' '}
                <span>{notif.verb}</span>{' '}
                {sharedLinkText && <span>'{sharedLinkText}'</span>}
              </div>
            </NotificationDescriptionTooltip>
            <div className="notification-menu__activity-time">{formattedTime}</div>
          </>
        ) : (
          <>
            {fallbackTitle && (
              <div className="notification-menu__activity-title">{fallbackTitle}</div>
            )}
            {fallbackDescription && (
              <NotificationDescriptionTooltip text={fallbackDescription}>
                <div className="notification-menu__activity-text">{fallbackDescription}</div>
              </NotificationDescriptionTooltip>
            )}
            <div className="notification-menu__activity-time">{formattedTime}</div>
          </>
        )}
      </div>

      <CardActions
        notif={notif}
        onMarkReadOrUnread={onMarkReadOrUnread}
        onDelete={onDelete}
      />
      {isShared && !notif.read && <span className="notification-menu__activity-dot notification-menu__activity-dot--right" aria-hidden="true" />}
    </div>
  );
});

export const NotificationPanel = React.memo(function NotificationPanel({
  isOpen,
  onClose,
}: NotificationPanelProps) {
  const [filterBy, setFilterBy] = useState<FilterKey>('all');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<InstanceType<NonNullable<BrowserWindowWithSpeech['SpeechRecognition']>> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    loading,
    error,
    markRead,
    markAllRead,
    deleteNotif,
    deleteAll,
    clearError,
    retry,
    refresh,
  } = useNotifications();

  useEffect(() => {
    if (isOpen) refresh();
  }, [isOpen, refresh]);

  const filtered = useMemo(() => {
    let items = [...notifications];

    // Unread tab should only contain pending approvals from shared agents/prompts.
    if (filterBy === 'unread') {
      items = items.filter(
        n =>
          !n.read &&
          n.kind === 'shared' &&
          (n.resourceType === 'prompt' || n.resourceType === 'agent')
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(n =>
        (n.title?.toLowerCase().includes(q)) ||
        (n.description?.toLowerCase().includes(q)) ||
        (n.sender?.toLowerCase().includes(q)) ||
        (n.linkText?.toLowerCase().includes(q)) ||
        (n.agentName?.toLowerCase().includes(q))
      );
    }

    return items;
  }, [notifications, filterBy, searchQuery]);

  useEffect(() => {
    const handleEscape = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        setFilterDropdownOpen(false);
        setMoreMenuOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const closeMenusOnOutside = (evt: MouseEvent) => {
      if (!panelRef.current?.contains(evt.target as Node)) {
        setFilterDropdownOpen(false);
        setMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeMenusOnOutside);
    return () => document.removeEventListener('mousedown', closeMenusOnOutside);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  const handleFilterSelect = useCallback((key: FilterKey) => {
    setFilterBy(key);
    setFilterDropdownOpen(false);
  }, []);

  const handleMarkAllRead = useCallback(() => {
    void markAllRead();
    setMoreMenuOpen(false);
  }, [markAllRead]);

  const handleDeleteAll = useCallback(async () => {
    await deleteAll();
    setShowDeleteConfirm(false);
  }, [deleteAll]);

  const handleVoiceSearch = useCallback(() => {
    const browserWindow = window as BrowserWindowWithSpeech;
    const Recognition = browserWindow.SpeechRecognition || browserWindow.webkitSpeechRecognition;

    if (!Recognition) {
      clearError();
      setIsListening(false);
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new Recognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript?.trim() ?? '';
      if (transcript) {
        setSearchQuery(transcript);
      }
    };
    recognition.onerror = (event) => {
      const voiceError = event.error === 'not-allowed'
        ? 'Microphone permission is required for voice search'
        : 'Voice search failed';
      setIsListening(false);
      console.error('[Notifications] Voice search error:', voiceError);
    };
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    setIsListening(true);
    recognition.start();
  }, [isListening, clearError]);

  if (!isOpen) return null;

  return (
    <div className="notification-menu notification-menu--open" id="notificationMenu" ref={panelRef}>
      <div className="notification-menu__header">
        <span className="notification-menu__title">Notifications</span>

        <div style={{ position: 'relative' }}>
          <button
            className="notification-menu__more-btn"
            type="button"
            title="More"
            aria-label="More notification actions"
            aria-haspopup="menu"
            aria-expanded={moreMenuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMoreMenuOpen(v => !v);
            }}
          >
            <span className="material-symbols-outlined">more_vert</span>
          </button>
          {moreMenuOpen && (
            <div
              className="notification-menu__card-action-menu notification-menu__header-action-menu"
              role="menu"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="notification-menu__card-action-item notification-menu__card-action-item--mark"
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAllRead();
                }}
              >
                <svg
                  className="pulse-unread-ico"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.85 15C7.36667 15 7.82917 14.85 8.2375 14.55C8.64583 14.25 8.93333 13.8583 9.1 13.375L9.475 12.225C9.74167 11.425 9.675 10.6875 9.275 10.0125C8.875 9.3375 8.3 9 7.55 9H4.025L4.5 12.925C4.58333 13.5083 4.84583 14 5.2875 14.4C5.72917 14.8 6.25 15 6.85 15ZM17.15 15C17.75 15 18.2708 14.8 18.7125 14.4C19.1542 14 19.4167 13.5083 19.5 12.925L19.975 9H16.475C15.725 9 15.15 9.34167 14.75 10.025C14.35 10.7083 14.2833 11.45 14.55 12.25L14.9 13.375C15.0667 13.8583 15.3542 14.25 15.7625 14.55C16.1708 14.85 16.6333 15 17.15 15ZM6.85 17C5.75 17 4.7875 16.6375 3.9625 15.9125C3.1375 15.1875 2.65833 14.275 2.525 13.175L2 9H1V7H7.55C8.28333 7 8.95417 7.17917 9.5625 7.5375C10.1708 7.89583 10.65 8.38333 11 9H13.025C13.375 8.38333 13.8542 7.89583 14.4625 7.5375C15.0708 7.17917 15.7417 7 16.475 7H23V9H22L21.475 13.175C21.3417 14.275 20.8625 15.1875 20.0375 15.9125C19.2125 16.6375 18.25 17 17.15 17C16.2 17 15.3458 16.7292 14.5875 16.1875C13.8292 15.6458 13.3 14.925 13 14.025L12.625 12.9C12.5917 12.7833 12.5583 12.6625 12.525 12.5375C12.4917 12.4125 12.4583 12.2333 12.425 12H11.575C11.5417 12.2 11.5083 12.3625 11.475 12.4875C11.4417 12.6125 11.4083 12.7333 11.375 12.85L11 14C10.7 14.9 10.1708 15.625 9.4125 16.175C8.65417 16.725 7.8 17 6.85 17Z"
                    fill="currentColor"
                  />
                </svg>
                <span>Mark all as read</span>
              </button>
              <div className="notification-menu__card-action-divider" />
              <button
                className="notification-menu__card-action-item notification-menu__card-action-item--delete"
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setMoreMenuOpen(false);
                }}
              >
                <span className="material-symbols-outlined">delete</span>
                <span>Delete All</span>
              </button>
            </div>
          )}
        </div>

        <button
          className="notification-menu__close-btn"
          type="button"
          title="Close"
          aria-label="Close notifications"
          onClick={onClose}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="notification-menu__search-bar utility-panel__search-bar">
        <div className="utility-panel__search-input" aria-label="Search notifications">
          <span className="material-symbols-outlined">search</span>
          <input
            type="text"
            placeholder="Search"
            autoComplete="off"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="notification-menu__search-clear"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          )}
          <button
            type="button"
            className={`notification-menu__voice-btn${isListening ? ' notification-menu__voice-btn--active' : ''}`}
            aria-label="Voice search"
            title="Voice search"
            onClick={handleVoiceSearch}
          >
            <span className="material-symbols-outlined">mic</span>
          </button>
        </div>
        <div className="utility-panel__search-filters">
          <div style={{ position: 'relative' }}>
            <button
              className="utility-panel__sort-btn"
              type="button"
              aria-haspopup="listbox"
              aria-expanded={filterDropdownOpen}
              onClick={(e) => {
                e.stopPropagation();
                setFilterDropdownOpen(v => !v);
              }}
            >
              <span className="material-symbols-outlined">filter_list</span>
              <span>{filterBy === 'all' ? 'All' : 'Unread'}</span>
              <span className="material-symbols-outlined">expand_more</span>
            </button>
            {filterDropdownOpen && (
              <div
                className="utility-panel__sort-dropdown"
                role="menu"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className={`utility-panel__sort-dropdown-item${filterBy === 'all' ? ' utility-panel__sort-dropdown-item--active' : ''}`}
                  data-notif-filter="all"
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterSelect('all');
                  }}
                >
                  <span>All</span>
                  <span className="material-symbols-outlined" aria-hidden="true">check</span>
                </button>
                <button
                  className={`utility-panel__sort-dropdown-item${filterBy === 'unread' ? ' utility-panel__sort-dropdown-item--active' : ''}`}
                  data-notif-filter="unread"
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterSelect('unread');
                  }}
                >
                  <span>Unread</span>
                  <span className="material-symbols-outlined" aria-hidden="true">check</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="notification-menu__body">
        {error && (
          <div className="notification-menu__error-state">
            <span className="material-symbols-outlined notification-menu__error-icon">error_outline</span>
            <p className="notification-menu__error-msg">{error.message}</p>
            <button
              type="button"
              className="notification-menu__retry-btn"
              onClick={() => {
                clearError();
                retry();
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!error && loading && (
          <div className="notification-menu__loading" aria-live="polite" aria-busy="true">
            {[1, 2, 3].map(i => (
              <div key={i} className="notification-menu__skeleton-row">
                <div className="notification-menu__skeleton-dot" />
                <div className="notification-menu__skeleton-avatar" />
                <div className="notification-menu__skeleton-content">
                  <div className="notification-menu__skeleton-line notification-menu__skeleton-line--title" />
                  <div className="notification-menu__skeleton-line" />
                  <div className="notification-menu__skeleton-line notification-menu__skeleton-line--time" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!error && !loading && filtered.length === 0 && (
          <div className="notification-menu__empty-state">
            <span className="material-symbols-outlined notification-menu__empty-icon">notifications_off</span>
            <p className="notification-menu__empty-msg">
              {searchQuery
                ? 'No notifications match your search'
                : filterBy === 'unread'
                  ? 'All caught up! No unread notifications.'
                  : 'No notifications yet.'}
            </p>
          </div>
        )}

        {!error && !loading && filtered.map(notif => (
          <NotificationCard
            key={notif.id}
            notif={notif}
            onMarkReadOrUnread={markRead}
            onDelete={deleteNotif}
          />
        ))}
      </div>

      {showDeleteConfirm && (
        <div className="notification-menu__dialog-overlay" role="dialog" aria-modal="true">
          <div className="notification-menu__dialog">
            <h3 className="notification-menu__dialog-title">Delete all notifications?</h3>
            <p className="notification-menu__dialog-body">Are you sure you want to delete all notifications?</p>
            <div className="notification-menu__dialog-actions">
              <button
                type="button"
                className="notification-menu__dialog-confirm"
                onClick={() => void handleDeleteAll()}
              >
                Delete All
              </button>
              <button
                type="button"
                className="notification-menu__dialog-cancel"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {isListening && (
        <div className="notification-menu__voice-hint" aria-live="polite">
          Listening...
        </div>
      )}
    </div>
  );
});

export default NotificationPanel;
